import React, { useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { Tag, Check, AlertCircle, Loader2, X } from "lucide-react";
import { validateCoupon } from "../../utils/organization";

export default function CouponInput({
  label = "Have a Coupon?",
  helperText = null,
  placeholder = "Enter coupon code",
  value = "",
  onChange,
  onDiscountApplied,
  appliedDiscount = null,
  disabled = false,
  className = "",
  labelClassName = "",
}) {
  const { getIdTokenClaims } = useAuth0();
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const handleApply = async () => {
    const trimmed = String(value || "").trim();
    if (!trimmed) {
      setErrorMsg("Please enter a coupon code");
      return;
    }

    setLoading(true);
    setErrorMsg("");

    try {
      const token = await getIdTokenClaims();
      const res = await validateCoupon(token.__raw, trimmed);

      if (res.data?.valid) {
        setErrorMsg("");
        onDiscountApplied?.(res.data.data);
      } else {
        setErrorMsg(res.data?.message || "Invalid or expired coupon code");
        onDiscountApplied?.(null);
      }
    } catch (err) {
      setErrorMsg(
        err.response?.data?.message || "Unable to validate coupon code"
      );
      onDiscountApplied?.(null);
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    onChange?.("");
    setErrorMsg("");
    onDiscountApplied?.(null);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleApply();
    }
  };

  return (
    <div className={`coupon-input-container ${className}`}>
      {label && (
        <label
          className={
            labelClassName ||
            "block text-xs font-semibold uppercase tracking-wider text-gray-600 mb-1 flex items-center gap-1.5"
          }
        >
          <Tag className="h-3.5 w-3.5 text-gray-500" />
          {label}
        </label>
      )}
      {helperText && (
        <p className="text-xs text-gray-500 mb-2 leading-relaxed">
          {helperText}
        </p>
      )}

      {appliedDiscount ? (
        <div className="flex items-center justify-between rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
          <div className="flex items-center gap-2">
            <Check className="h-4 w-4 text-emerald-600 shrink-0" />
            <span>
              <strong className="font-semibold">{appliedDiscount.code || value}</strong>
              {appliedDiscount.percent_off != null && (
                <span className="ml-1 text-emerald-700">
                  ({appliedDiscount.percent_off}% off)
                </span>
              )}
              {appliedDiscount.amount_off != null && (
                <span className="ml-1 text-emerald-700">
                  (${Number(appliedDiscount.amount_off).toFixed(2)} off)
                </span>
              )}
              {appliedDiscount.duration && (
                <span className="ml-1.5 rounded bg-emerald-100 px-1.5 py-0.5 text-[11px] font-medium text-emerald-800">
                  {appliedDiscount.duration === "once"
                    ? "1st month only"
                    : appliedDiscount.duration === "repeating"
                    ? `${appliedDiscount.duration_in_months ? appliedDiscount.duration_in_months + ' mo' : 'repeating'}`
                    : "Forever"}
                </span>
              )}
            </span>
          </div>
          <button
            type="button"
            onClick={handleClear}
            className="text-xs text-emerald-700 hover:text-emerald-900 font-medium ml-2 p-0.5"
            title="Remove coupon"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      ) : (
        <div>
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={value}
              onChange={(e) => {
                onChange?.(e.target.value.toUpperCase());
                if (errorMsg) setErrorMsg("");
              }}
              onKeyDown={handleKeyDown}
              disabled={disabled || loading}
              placeholder="Enter coupon code"
              className="flex-1 rounded-lg border border-gray-300 px-3 py-1.5 text-sm text-gray-900 placeholder:text-gray-400 focus:border-sky-500 focus:outline-none focus:ring-1 focus:ring-sky-500 uppercase disabled:bg-gray-100"
            />
            <button
              type="button"
              onClick={handleApply}
              disabled={disabled || loading || !String(value || "").trim()}
              className="inline-flex items-center justify-center rounded-lg bg-gray-900 px-3.5 py-1.5 text-sm font-medium text-white transition-colors hover:bg-gray-800 disabled:cursor-not-allowed disabled:opacity-40"
            >
              {loading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                "Apply"
              )}
            </button>
          </div>

          {errorMsg && (
            <p className="mt-1.5 flex items-center gap-1 text-xs text-rose-600 font-medium">
              <AlertCircle className="h-3.5 w-3.5 shrink-0" />
              {errorMsg}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
